import { useCallback, useEffect, useMemo, useState } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { Receipt, Eye } from "lucide-react";
import { Asset } from "../../types/supplier";
import type { Invoice as StoreInvoice } from "../../store/store";
import apiService from "../../services/apiService";
import { InvoiceViewSheet } from "../purchaseOrder/InvoiceDetailDialog";

const PAGE_SIZE = 25;

interface InvoicesTabProps {
  supplierID: string;
}

interface ApiResponse<T> {
  data: T;
  status?: number;
  message?: string;
}

interface AdminUser {
  id: number;
  name: string;
}

interface SupplierInvoiceRow extends StoreInvoice {
  entity_name?: string;
  paidAmount?: number;
}

const inr = (value: number | null | undefined) =>
  "₹" +
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value ?? 0);

const toNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const mapInvoice = (raw: Record<string, any>): SupplierInvoiceRow => {
  const amount = toNumber(raw.amount ?? raw.base_amount ?? raw.baseAmount);
  const tax = toNumber(raw.tax ?? raw.tax_amount ?? raw.taxAmount ?? raw.gst_amount);

  return {
    id: String(raw.id ?? ""),
    poId: String(raw.po_id ?? raw.poId ?? ""),
    number: String(raw.number ?? raw.invoiceNumber ?? raw.invoice_number ?? "-"),
    date: String(raw.date ?? raw.invoice_date ?? raw.created_at ?? ""),
    dueDate: raw.due_date ?? raw.dueDate ?? undefined,
    amount,
    tax,
    status: (raw.status || "pending") as StoreInvoice["status"],
    previousPurchaseValue: toNumber(raw.previousPurchaseValue ?? raw.previous_purchase_value),
    tds_amount: toNumber(raw.tds_amount ?? raw.tdsAmount),
    tds_exempted: toNumber(raw.tds_exempted ?? raw.tdsExempted),
    e_way_status: raw.e_way_status ?? raw.eWayStatus ?? null,
    entity_name: raw.entity_name ?? "",
    paidAmount: toNumber(raw.paidAmount ?? raw.paid_amount),
  };
};

const mapAsset = (raw: Record<string, any>, supplierID: string): Asset => ({
  id: String(raw.id ?? ""),
  assetId: String(raw.unique_asset_id ?? ""),
  assetType: String(raw.asset_type || "other"),
  poId: String(raw.po_id ?? ""),
  invoiceId: raw.invoice_id != null ? String(raw.invoice_id) : undefined,
  assetLineId: raw.asset_line_id != null ? String(raw.asset_line_id) : undefined,
  leaseStatus: "Active",
  supplierId: supplierID,
  assetName: String(raw.item || "-"),
  specification: raw.specification ?? "",
  qty: toNumber(raw.qty) || 1,
});

function InvoiceAssetDetails({
  assets,
  onOpenAsset,
}: {
  assets: Asset[];
  onOpenAsset?: () => void;
}) {
  if (!assets.length) return <span className="text-[#7c8b91]">—</span>;

  const groupsMap: Record<string, { name: string; specification?: string; qty: number }> = {};
  for (const asset of assets) {
    const key = String(asset.assetLineId ?? asset.id ?? "unknown");
    if (!groupsMap[key]) {
      groupsMap[key] = {
        name: asset.assetName || "Item",
        specification: asset.specification || "",
        qty: 0,
      };
    }
    groupsMap[key].qty += Number(asset.qty ?? 1) || 1;
  }

  const groups = Object.values(groupsMap);

  if (groups.length === 1) {
    const g = groups[0];
    return (
      <div className="min-w-[140px]">
        <button
          type="button"
          onClick={onOpenAsset}
          className="text-[13px] font-medium text-[#2e6b76] underline decoration-dotted underline-offset-3 hover:text-[#151b1d] cursor-pointer text-left"
        >
          {g.name}
        </button>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onOpenAsset}
            className="inline-flex cursor-pointer items-baseline gap-1 text-[13px] font-medium text-[#2e6b76] underline decoration-dotted underline-offset-3 hover:text-[#151b1d]"
          >
            <span className="truncate max-w-[140px]">{groups[0].name}</span>
            <span className="shrink-0 font-medium">+{groups.length - 1} more</span>
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs bg-white border border-[#dde3e4] text-[#151b1d] shadow-md p-2 rounded-[6px]">
          <div className="space-y-1 text-xs">
            {groups.map((g, idx) => (
              <div key={idx} className="text-[#151b1d]">
                <span className="font-semibold">{g.name}</span>
                {g.specification ? ` — ${g.specification}` : ""} — Qty: {g.qty}
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function InvoicesTab({ supplierID }: InvoicesTabProps) {
  const [invoices, setInvoices] = useState<SupplierInvoiceRow[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [adminUserList, setAdminUserList] = useState<AdminUser[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<SupplierInvoiceRow | null>(null);
  const [selectedInvoicePo, setSelectedInvoicePo] = useState<any>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchInvoicesAndAssets = useCallback(async () => {
    if (!supplierID) {
      setInvoices([]);
      setAssets([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [invoiceRes, assetRes] = await Promise.all([
        apiService.get<ApiResponse<Record<string, any>[]>>(
          `/supplier/get-invoice-by-supplier-id/${supplierID}`
        ),
        apiService.get<ApiResponse<Record<string, any>[]>>(
          `/supplier/get-individual-assets-by-supplier-id/${supplierID}`
        ),
      ]);

      setInvoices(
        invoiceRes.status === 200 && Array.isArray(invoiceRes.data)
          ? invoiceRes.data.map((inv) => mapInvoice(inv))
          : []
      );
      setAssets(
        assetRes.status === 200 && Array.isArray(assetRes.data)
          ? assetRes.data.map((a) => mapAsset(a, supplierID))
          : []
      );
    } catch {
      setInvoices([]);
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, [supplierID]);

  useEffect(() => {
    setCurrentPage(1);
    void fetchInvoicesAndAssets();
  }, [fetchInvoicesAndAssets]);

  useEffect(() => {
    const fetchAdminUsers = async () => {
      try {
        const response = await apiService.get<{ data: AdminUser[] }>(
          `/customers/get-admin-user-list`
        );
        setAdminUserList(
          Array.isArray(response.data)
            ? response.data.map((u) => ({ id: u.id, name: u.name }))
            : []
        );
      } catch {
        setAdminUserList([]);
      }
    };
    void fetchAdminUsers();
  }, []);

  const assetsByInvoiceId = useMemo(() => {
    const map = new Map<string, Asset[]>();
    for (const asset of assets) {
      if (!asset.invoiceId) continue;
      const list = map.get(asset.invoiceId) ?? [];
      list.push(asset);
      map.set(asset.invoiceId, list);
    }
    return map;
  }, [assets]);

  const getPOByInvoiceId = async (poOrInvoiceId: string) => {
    try {
      const response = await apiService.get<any>(
        `/purchase-order/get-purchase-order-by-invoiced-id/${poOrInvoiceId}`
      );
      return response?.data ?? null;
    } catch {
      return null;
    }
  };

  const handleInvoiceViewDetails = async (invoice: SupplierInvoiceRow) => {
    const fallbackPo = {
      id: invoice.poId,
      purchaseOrderCode: invoice.poId ? `PO-${invoice.poId}` : "—",
      supplierId: supplierID,
      customerId: undefined as string | undefined,
    };

    setSelectedInvoice(invoice);
    setSelectedInvoicePo(fallbackPo);
    setSheetOpen(false);
    requestAnimationFrame(() => setSheetOpen(true));

    const lookupId = invoice.poId || invoice.id;
    if (!lookupId) return;

    const poData = await getPOByInvoiceId(lookupId);
    if (!poData) return;

    setSelectedInvoicePo({
      ...poData,
      customerId: poData.customerId ?? poData.customer_id,
      supplierId: poData.supplierId ?? poData.supplier_id ?? supplierID,
      purchaseOrderCode:
        poData.purchaseOrderCode ?? poData.purchase_order_code ?? poData.po_number,
    });
  };

  const formatDate = (value?: string) => {
    if (!value) return "—";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "—";
    return parsed.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const totalPages = Math.max(1, Math.ceil(invoices.length / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedInvoices = useMemo(() => {
    return invoices.slice(startIndex, startIndex + PAGE_SIZE);
  }, [invoices, startIndex]);

  return (
    <div className="bg-white border border-[#dde3e4] rounded-[10px] p-5 shadow-[0_1px_2px_rgba(21,27,29,0.04),0_4px_10px_-6px_rgba(21,27,29,0.10)] font-['Poppins',sans-serif]">
      {/* Header */}
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-8 h-8 rounded-[8px] bg-[#F1F6FA] text-[#2e6b76] flex items-center justify-center font-semibold text-xs flex-shrink-0">
          <Receipt className="w-4 h-4" />
        </div>
        <div className="flex items-center gap-2">
          <h2 className="text-[15px] font-semibold text-[#151b1d]">
            Proforma invoices
          </h2>
          <span className="text-[10.5px] bg-[#f6f8f8] text-[#151b1d] px-1.5 py-0.2 rounded font-medium">
            {invoices.length}
          </span>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex h-56 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-[#dde3e4] border-t-[#3f8b98]" />
        </div>
      ) : invoices.length > 0 ? (
        <div className="border border-[#dde3e4] rounded-[8px] overflow-hidden bg-white">
          <div className="overflow-x-auto max-h-[560px]">
            <table className="w-full border-collapse text-left min-w-[900px]">
              <thead className="sticky top-0 z-10">
                <tr className="bg-[#F1F6FA] text-[#2e6b76] border-b border-[#dde3e4] text-[11px] font-semibold">
                  <th className="py-3 px-3.5 pl-4">Invoice number</th>
                  <th className="py-3 px-3.5">Company name</th>
                  <th className="py-3 px-3.5">Asset details</th>
                  <th className="py-3 px-3.5">Issued at</th>
                  <th className="py-3 px-3.5">Due date</th>
                  <th className="py-3 px-3.5 text-right">Invoice amount</th>
                  <th className="py-3 px-3.5 text-right">Paid amount</th>
                  <th className="py-3 px-3.5 text-center">Status</th>
                  <th className="py-3 pr-4 pl-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#eaeeef]">
                {paginatedInvoices.map((invoice, idx) => {
                  const totalAmt = invoice.amount + invoice.tax;
                  const paid = invoice.paidAmount ?? 0;
                  const isPaid = String(invoice.status).toLowerCase() === "paid" || paid >= totalAmt;
                  const statusLabel = isPaid ? "Paid" : "Pending";

                  return (
                    <tr
                      key={invoice.id || idx}
                      className="hover:bg-[#f1f7f8]/50 transition-colors text-[13px]"
                    >
                      <td className="py-3 px-3.5 pl-4 font-semibold text-[#151b1d]">
                        {invoice.number && invoice.number !== "-" ? invoice.number : "—"}
                      </td>
                      <td className="py-3 px-3.5 text-[#526066]">
                        {invoice.entity_name?.trim() || "—"}
                      </td>
                      <td className="py-3 px-3.5">
                        <InvoiceAssetDetails
                          assets={assetsByInvoiceId.get(invoice.id) ?? []}
                          onOpenAsset={() => void handleInvoiceViewDetails(invoice)}
                        />
                      </td>
                      <td className="py-3 px-3.5 text-[#7c8b91] whitespace-nowrap">
                        {formatDate(invoice.date)}
                      </td>
                      <td className="py-3 px-3.5 text-[#7c8b91] whitespace-nowrap">
                        {formatDate(invoice.dueDate)}
                      </td>
                      <td className="py-3 px-3.5 text-right font-semibold text-[#151b1d] tabular-nums whitespace-nowrap">
                        {inr(totalAmt)}
                      </td>
                      <td className="py-3 px-3.5 text-right font-semibold text-[#7c8b91] tabular-nums whitespace-nowrap">
                        {inr(paid)}
                      </td>
                      <td className="py-3 px-3.5 text-center whitespace-nowrap">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-[6px] text-[11px] font-semibold tracking-wide uppercase ${
                            isPaid
                              ? "bg-[#e2f2e8] text-[#227a4c]"
                              : "bg-[#f3e7c9] text-[#8c6500]"
                          }`}
                        >
                          {statusLabel}
                        </span>
                      </td>
                      <td className="py-3 pr-4 pl-3 text-right">
                        <button
                          type="button"
                          onClick={() => void handleInvoiceViewDetails(invoice)}
                          className="w-7 h-7 rounded-[6px] text-[#7c8b91] hover:text-[#3f8b98] hover:bg-[#e1f0f1] inline-flex items-center justify-center transition-colors cursor-pointer"
                          title="View invoice"
                          aria-label="View invoice"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="flex items-center justify-between border-t border-[#dde3e4] px-4 py-3 bg-white text-xs text-[#526066]">
            <div>
              {invoices.length > 0
                ? `Showing ${startIndex + 1}–${Math.min(startIndex + PAGE_SIZE, invoices.length)} of ${invoices.length}`
                : "Showing 0 of 0"}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="min-w-[28px] h-7 px-2 border border-[#dde3e4] rounded-[6px] bg-white text-[#151b1d] hover:border-[#3f8b98] disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium cursor-pointer"
                >
                  ‹
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;

                  const isActive = currentPage === pageNum;
                  return (
                    <button
                      key={pageNum}
                      type="button"
                      onClick={() => setCurrentPage(pageNum)}
                      className={`min-w-[28px] h-7 px-2 rounded-[6px] text-xs font-medium cursor-pointer transition-colors ${
                        isActive
                          ? "bg-[#3f8b98] border border-[#3f8b98] text-white"
                          : "bg-white border border-[#dde3e4] text-[#151b1d] hover:border-[#3f8b98]"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="min-w-[28px] h-7 px-2 border border-[#dde3e4] rounded-[6px] bg-white text-[#151b1d] hover:border-[#3f8b98] disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium cursor-pointer"
                >
                  ›
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-[#7c8b91]">
          <Receipt className="w-10 h-10 opacity-30 mb-2" />
          <p className="text-sm font-semibold text-[#151b1d]">No invoices found</p>
          <p className="text-xs text-[#7c8b91] mt-0.5">Invoices from this supplier will appear here</p>
        </div>
      )}

      {/* Invoice Detail Sheet */}
      {selectedInvoice && (
        <InvoiceViewSheet
          key={selectedInvoice.id}
          invoice={selectedInvoice}
          po={selectedInvoicePo}
          open={sheetOpen}
          onOpenChange={(open) => {
            setSheetOpen(open);
            if (!open) {
              setSelectedInvoice(null);
              setSelectedInvoicePo(null);
            }
          }}
          addPayment={() => {
            void fetchInvoicesAndAssets();
          }}
          adminUserList={adminUserList}
        />
      )}
    </div>
  );
}
